// Contacts: the list, one contact, edits and CSV import. Every role reads;
// owners and admins change, and the server enforces that.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import type { components } from './generated/schema'

export type Contact = components['schemas']['Contact']
export type ContactPage = components['schemas']['ContactPage']
export type ContactUpdate = components['schemas']['ContactUpdate']
export type ContactImport = components['schemas']['ContactImport']
export type ContactImportResult = components['schemas']['ContactImportResult']
export type OptInStatus = components['schemas']['OptInStatus']

export type ContactFilter = {
  q: string
  tagId?: string | undefined
  optIn?: OptInStatus | undefined
  suppressed?: boolean | undefined
}

const keys = {
  all: ['contacts'] as const,
  list: (f: ContactFilter) => ['contacts', 'list', f] as const,
  one: (id: string) => ['contacts', 'one', id] as const,
}

export function useContacts(f: ContactFilter) {
  return useInfiniteQuery({
    queryKey: keys.list(f),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ContactPage> =>
      unwrap(
        await api.GET('/contacts', {
          params: {
            query: {
              ...(f.q ? { q: f.q } : {}),
              ...(f.tagId ? { tagId: f.tagId } : {}),
              ...(f.optIn ? { optIn: f.optIn } : {}),
              ...(f.suppressed !== undefined ? { suppressed: f.suppressed } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useContact(id: string) {
  return useQuery({
    queryKey: keys.one(id),
    queryFn: async (): Promise<Contact> => unwrap(await api.GET('/contacts/{contactId}', { params: { path: { contactId: id } } })),
  })
}

// A contact change can alter inbox rows too (the name shows there).
function useRefreshContacts() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: keys.all })
    void qc.invalidateQueries({ queryKey: ['conversations'] })
    void qc.invalidateQueries({ queryKey: ['conversation'] })
    void qc.invalidateQueries({ queryKey: ['tags'] })
  }
}

export function useUpdateContact(id: string) {
  const refresh = useRefreshContacts()
  return useMutation({
    mutationFn: async (body: ContactUpdate) =>
      unwrap(await api.PATCH('/contacts/{contactId}', { params: { path: { contactId: id } }, body })),
    onSuccess: refresh,
  })
}

export function useImportContacts() {
  const refresh = useRefreshContacts()
  return useMutation({
    mutationFn: async (body: ContactImport): Promise<ContactImportResult> => unwrap(await api.POST('/contacts/import', { body })),
    onSuccess: refresh,
  })
}

export const optInLabel: Record<OptInStatus, string> = {
  unknown: 'No consent recorded',
  optedIn: 'Opted in',
  optedOut: 'Opted out',
}
